// src/components/AccessibilityMenu.js
import React, { useState, useEffect } from 'react';
import {Accessibility, Type, ZoomIn, Contrast, X } from 'lucide-react';

const AccessibilityMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [highContrast, setHighContrast] = useState(false);
  const [grayscale, setGrayscale] = useState(false);
  const [linkHighlight, setLinkHighlight] = useState(false);

  // שמירת ההעדפות ב-localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setFontSize(settings.fontSize);
      setHighContrast(settings.highContrast);
      setGrayscale(settings.grayscale);
      setLinkHighlight(settings.linkHighlight);
      applySettings(settings);
    }
  }, []);

  const saveSettings = (settings) => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
  };

  const applySettings = (settings) => {
    // הגדרת גודל טקסט
    document.documentElement.style.fontSize = `${settings.fontSize}%`;

    // הגדרת ניגודיות גבוהה
    if (settings.highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }

    // הגדרת גווני אפור
    if (settings.grayscale) {
      document.body.classList.add('grayscale');
    } else {
      document.body.classList.remove('grayscale');
    }

    // הדגשת קישורים
    if (settings.linkHighlight) {
      document.body.classList.add('highlight-links');
    } else {
      document.body.classList.remove('highlight-links');
    }
  };

  const handleSettingChange = (setting, value) => {
    const newSettings = {
      fontSize,
      highContrast,
      grayscale,
      linkHighlight,
      [setting]: value
    };

    switch (setting) {
      case 'fontSize':
        setFontSize(value);
        break;
      case 'highContrast':
        setHighContrast(value);
        break;
      case 'grayscale':
        setGrayscale(value);
        break;
      case 'linkHighlight':
        setLinkHighlight(value);
        break;
      default:
        break;
    }

    applySettings(newSettings);
    saveSettings(newSettings);
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      highContrast: false,
      grayscale: false,
      linkHighlight: false
    };

    setFontSize(defaultSettings.fontSize);
    setHighContrast(defaultSettings.highContrast);
    setGrayscale(defaultSettings.grayscale);
    setLinkHighlight(defaultSettings.linkHighlight);
    applySettings(defaultSettings);
    saveSettings(defaultSettings);
  };

  return (
    <>
      {/* כפתור נגישות קבוע */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 z-50"
        aria-label="פתח תפריט נגישות"
      >
        <Accessibility className="w-6 h-6" />
      </button>

      {/* תפריט נגישות */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-sm w-full relative">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              aria-label="סגור תפריט נגישות"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl font-bold mb-6 text-center">הגדרות נגישות</h2>

            <div className="space-y-6">
              {/* גודל טקסט */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-medium flex items-center gap-2">
                    <Type className="w-5 h-5" />
                    גודל טקסט
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSettingChange('fontSize', Math.max(80, fontSize - 10))}
                      className="p-1 rounded-md hover:bg-gray-100"
                      aria-label="הקטן טקסט"
                    >
                      A-
                    </button>
                    <span className="w-8 text-center">{fontSize}%</span>
                    <button
                      onClick={() => handleSettingChange('fontSize', Math.min(150, fontSize + 10))}
                      className="p-1 rounded-md hover:bg-gray-100"
                      aria-label="הגדל טקסט"
                    >
                      A+
                    </button>
                  </div>
                </div>
              </div>

              {/* ניגודיות */}
              <div className="flex items-center justify-between">
                <label className="font-medium flex items-center gap-2">
                  <Contrast className="w-5 h-5" />
                  ניגודיות גבוהה
                </label>
                <button
                  onClick={() => handleSettingChange('highContrast', !highContrast)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                    ${highContrast ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${highContrast ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>

              {/* מצב גווני אפור */}
              <div className="flex items-center justify-between">
                <label className="font-medium flex items-center gap-2">
                  מצב גווני אפור
                </label>
                <button
                  onClick={() => handleSettingChange('grayscale', !grayscale)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                    ${grayscale ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${grayscale ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>

              {/* הדגשת קישורים */}
              <div className="flex items-center justify-between">
                <label className="font-medium flex items-center gap-2">
                  הדגשת קישורים
                </label>
                <button
                  onClick={() => handleSettingChange('linkHighlight', !linkHighlight)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                    ${linkHighlight ? 'bg-blue-600' : 'bg-gray-200'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${linkHighlight ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>

              {/* כפתור איפוס */}
              <button
                onClick={resetSettings}
                className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                איפוס הגדרות
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityMenu;